import {
  Button,
  DateRange,
  Dropdown,
  Field,
  Intro,
  Text,
  TextField,
  DropdownPaginatedSingleInvoicing,
} from "../../../components/commons";
import locale from "../../../localization";
import styles from "./single-invoicing.module.scss";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useApi, useForm, useModal } from "../../../hooks";
import { useRouter } from "../../../hooks";
import { createInvoiceGuide, getFleetByName, getInvoiceTransactions, updateInvoiceExemptions } from "../../../apis";
import { initialState } from "./single-invoicing.state";
import SingleInvoicingGuideModal from "./single-invoicing-guide.modal";
import { debounced } from "../../../utils";
import { ConfirmModal } from "../../../components/modals";
import { isAlphaNumeric } from "../../../utils/text.utils";
import HistoryIcon from "@material-ui/icons/History";
import { Path, ProductSelectionEnum } from "../../../enums";
import {
  COMPANY_OPTIONS,
  INVOICE_TYPE_OPTIONS,
  MONTH_OPTIONS,
  PRODUCT_OPTIONS,
  SEARCH_MAX_LENGTH,
  VAT_DIVISOR,
  VAT_RATE,
} from "./single-invoicing.constants";
import {
  areSelectionsEqual,
  formatPeriodDisplay,
  formatPeriodFromApi,
  getChangedSelectionCount,
  getRoundedNumber,
  getSearchableLabel,
  getSelectedPeriod,
  getTransactionAmountWithCurrency,
  getTransactionCompany,
  getTransactionDate,
  getTransactionDateShortLabel,
  getTransactionId,
  getTransactionProduct,
  getTransactionsFromResponse,
  isExemptedTransaction,
  normalizeSelection,
  toNumber,
} from "./single-invoicing.utils";

const PLBSingleInvoicingModule = () => {
  const { push } = useRouter();
  const [fleet, setFleet] = useState();
  const [company, setCompany] = useState(null);
  const [product, setProduct] = useState(ProductSelectionEnum.ALL_PRODUCTS);
  const [transactionOptions, setTransactionOptions] = useState([]);
  const [exemptedTransactions, setExemptedTransactions] = useState([]);
  const [appliedExemptedTransactions, setAppliedExemptedTransactions] = useState([]);
  const [changedSelectionCount, setChangedSelectionCount] = useState(0);
  const [closeExemptedDropdownToken, setCloseExemptedDropdownToken] = useState(0);
  const [isCustomEndDateSelected, setIsCustomEndDateSelected] = useState(false);
  const [createdInvoiceGuide, setCreatedInvoiceGuide] = useState(null);
  const invoiceGuideModal = useModal();

  const customInvoiceModal = useModal();
  const exemptedTransactionModal = useModal();

  const { request: getFleetRequest, loading: loadingFleetDetails } = useApi({
    api: getFleetByName,
    handleOwnError: true,
  });

  const { request: getInvoiceTransactionsRequest, loading: loadingInvoiceTransactions } = useApi({
    api: getInvoiceTransactions,
    handleOwnError: true,
  });

  const { request: createInvoiceGuideRequest, loading: creatingInvoiceGuide } = useApi({
    api: createInvoiceGuide,
    handleOwnError: true,
  });
  const { request: updateExemptionsRequest } = useApi({
    api: updateInvoiceExemptions,
    handleOwnError: true,
  });
  const {
    fields = {},
    modifyField,
    applyFieldErrors,
  } = useForm({
    initialState,
  });

  const yearOptions = (() => {
    const startYear = 2026;
    const currentYear = new Date().getFullYear();
    const years = [];

    for (let year = currentYear; year >= startYear; year--) {
      years.push({ label: `${year}`, value: `${year}` });
    }

    return years;
  })();

  const fetchFleetDetails = useCallback(
    async (params) => {
      try {
        const res = await getFleetRequest(params);
        console.log('business', res);
        const invoicingSetup = res.invoicingSetup;
        if (res && res.status !== "active") {
          setFleet(null);
          applyFieldErrors({
            businessId: "Business ID not found",
          });
        } else if (invoicingSetup !== 'single-invoicing') {
          setFleet(null);
          applyFieldErrors({
            businessId: "This business ID is not under single invoicing",
          });
        }
        else {
          setFleet(res);
          modifyField("businessName", { value: res?.businessName || "" });
        }
      } catch (e) {
        setFleet(null);
        applyFieldErrors({
          businessId: "Business ID not found",
        });
      }
    },
    [getFleetRequest, applyFieldErrors, modifyField]
  );

  const { businessId, businessName, invoiceType, invoiceMonth, invoiceYear, invoicePeriod } = fields;

  const onChangeBusinessIdCb = useCallback(
    (fieldName, { value }) => {
      if (!isAlphaNumeric(value)) {
        return;
      }

      modifyField(fieldName, { value });

      setFleet(null);
      setCompany(null);
      setProduct(ProductSelectionEnum.ALL_PRODUCTS);
      setTransactionOptions([]);
      setExemptedTransactions([]);
      setAppliedExemptedTransactions([]);
      setIsCustomEndDateSelected(false);
    },
    [modifyField]
  );

  const selectedPeriod = useMemo(() => {
    return getSelectedPeriod({
      invoiceType,
      invoiceMonth,
      invoiceYear,
      invoicePeriod,
    });
  }, [invoiceType, invoiceMonth, invoiceYear, invoicePeriod]);

  const shouldShowTransactionFields = Boolean(selectedPeriod?.periodStart && selectedPeriod?.periodEnd);

  const clearTransactions = useCallback(() => {
    setTransactionOptions((prev) => (prev.length ? [] : prev));
    setExemptedTransactions((prev) => (prev.length ? [] : prev));
    setAppliedExemptedTransactions((prev) => (prev.length ? [] : prev));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(
    debounced(async () => {
      if (!businessId.error && businessId.value?.trim()) {
        await fetchFleetDetails({ name: businessId.value });
      }
    }, 1000),
    [businessId.value, businessId.error]
  );

  useEffect(() => {
    if (invoiceType?.value === "custom") {
      setIsCustomEndDateSelected(false);
      customInvoiceModal.show({});
    }
  }, [invoiceType?.value]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const shortName = fleet?.shortName || businessId?.value;
    const canFetchCustomInvoiceTransactions =
      invoiceType?.value !== "custom" || isCustomEndDateSelected;

    if (
      !shortName ||
      !selectedPeriod.periodStart ||
      !selectedPeriod.periodEnd ||
      !canFetchCustomInvoiceTransactions
    ) {
      clearTransactions();
      return;
    }

    let disposed = false;

    const fetchInvoiceTransactions = async () => {
      try {
        const res = await getInvoiceTransactionsRequest({
          shortName,
          periodStart: selectedPeriod.periodStart,
          periodEnd: selectedPeriod.periodEnd,
        });

        if (disposed) {
          return;
        }

        const transactions = getTransactionsFromResponse(res);

        const sortedTransactions = [...transactions].sort((a, b) => {
          const dateA = new Date(getTransactionDate(a) || 0).getTime();
          const dateB = new Date(getTransactionDate(b) || 0).getTime();
          return dateB - dateA;
        });

        const options = sortedTransactions.map((transaction, index) => {
          const shortDateLabel = getTransactionDateShortLabel(transaction);
          const transactionId = getTransactionId(transaction, index);
          const productLabel = getTransactionProduct(transaction);
          const amountLabel = getTransactionAmountWithCurrency(transaction);
          const optionText = `${shortDateLabel} ${transactionId} - ${productLabel}: ${amountLabel}`;

          return {
            value: transactionId,
            label: getSearchableLabel(transaction),
            transaction,
            customLabel: optionText,
            isExempted: isExemptedTransaction(transaction),
          };
        });

        const defaultExempted = normalizeSelection(
          options.filter((option) => option.isExempted).map((option) => ({ value: option.value, label: option.label }))
        );

        setTransactionOptions(options);
        setExemptedTransactions(defaultExempted);
        setAppliedExemptedTransactions(defaultExempted);
      } catch (error) {
        if (!disposed) {
          clearTransactions();
        }
      }
    };

    fetchInvoiceTransactions().then(_r => {});

    return () => {
      disposed = true;
    };
  }, [
    businessId?.value,
    clearTransactions,
    fleet?.shortName,
    invoiceType?.value,
    isCustomEndDateSelected,
    selectedPeriod.periodEnd,
    selectedPeriod.periodStart,
  ]);

  const handleExemptionChange = useCallback(
    (nextSelection) => {
      setExemptedTransactions(nextSelection);
    },
    []
  );

  const handleExemptedCloseAttempt = useCallback(() => {
    const hasChanges = !areSelectionsEqual(appliedExemptedTransactions, exemptedTransactions);

    if (!hasChanges) {
      return true;
    }

    setChangedSelectionCount(
      getChangedSelectionCount(appliedExemptedTransactions, exemptedTransactions)
    );
    exemptedTransactionModal.show({});
    return false;
  }, [appliedExemptedTransactions, exemptedTransactions, exemptedTransactionModal]);

  const onCancelExemptedChanges = () => {
    setExemptedTransactions(normalizeSelection(appliedExemptedTransactions));
    setCloseExemptedDropdownToken((prev) => prev + 1);
    exemptedTransactionModal.close();
  };

  const onProceedExemptedChanges = async () => {
    const nextSelection = exemptedTransactions;
    const nextSet = new Set(nextSelection.map((s) => `${s.value}`));
    const prevSelection = appliedExemptedTransactions;
    const prevSet = new Set(prevSelection.map((s) => `${s.value}`));

    const changedValues = [
      ...nextSelection.filter((s) => !prevSet.has(`${s.value}`)).map((s) => s.value),
      ...prevSelection.filter((s) => !nextSet.has(`${s.value}`)).map((s) => s.value),
    ];

    const tags = changedValues
      .map((v) => transactionOptions.find((o) => `${o.value}` === `${v}`))
      .filter((o) => o && o.transaction?.source)
      .map((o) => ({
        source: o.transaction.source,
        isReceiptIssued: nextSet.has(`${o.value}`),
        transactionId: Number(o.value),
      }));

    setAppliedExemptedTransactions(normalizeSelection(nextSelection));
    setCloseExemptedDropdownToken((prev) => prev + 1);
    exemptedTransactionModal.close();

    if (!tags.length) {
      return;
    }

    try {
      await updateExemptionsRequest({ transactions: tags });
    } catch (error) {
      setExemptedTransactions(normalizeSelection(prevSelection));
      setAppliedExemptedTransactions(normalizeSelection(prevSelection));
    }
  };

  const isMonthlySelected = invoiceType?.value === "monthly";
  const isCustomSelected = invoiceType?.value === "custom";
  // const isDateFilled = Boolean(
  //   selectedPeriod?.periodStart &&
  //     selectedPeriod?.periodEnd &&
  //     (invoiceType?.value !== "custom" || isCustomEndDateSelected)
  // );

  const invoiceDateLabel = useMemo(() => {
    return formatPeriodDisplay({
      invoiceType,
      invoiceMonth,
      invoiceYear,
      invoicePeriod,
    });
  }, [invoiceMonth, invoicePeriod, invoiceType, invoiceYear]);

  const selectedCompanyLabel = useMemo(() => {
    return COMPANY_OPTIONS.find((option) => option.value === company)?.label || "";
  }, [company]);

  const selectedProductLabel = useMemo(() => {
    return PRODUCT_OPTIONS.find((option) => option.value === product)?.label || "";
  }, [product]);

  const filteredInvoiceTransactions = useMemo(() => {
    if (!selectedCompanyLabel || !selectedProductLabel) {
      return [];
    }

    const exemptedTransactionIds = new Set(exemptedTransactions.map((item) => item.value));

    return transactionOptions.filter((option) => {
      const transaction = option?.transaction || {};
      const transactionCompany = getTransactionCompany(transaction).toLowerCase();
      const transactionProduct = getTransactionProduct(transaction).toLowerCase();

      const companyMatched = transactionCompany === selectedCompanyLabel.toLowerCase();
      const productMatched =
        product === ProductSelectionEnum.ALL_PRODUCTS ||
        transactionProduct === selectedProductLabel.toLowerCase();
      const isExempted = exemptedTransactionIds.has(option.value);

      return companyMatched && productMatched && !isExempted;
    });
  }, [
    exemptedTransactions,
    product,
    selectedCompanyLabel,
    selectedProductLabel,
    transactionOptions,
  ]);

  const invoiceSummary = useMemo(() => {
    return filteredInvoiceTransactions.reduce(
      (acc, option) => {
        const transaction = option?.transaction || {};

        acc.fuelPurchased += toNumber(transaction?.total_sales) + toNumber(transaction?.amount);
        acc.totalLiters += toNumber(transaction?.liter_volume);
        acc.discounts +=
          toNumber(transaction?.total_user_discount_premium) + toNumber(transaction?.total_discount);
        acc.plbCreditAmount += toNumber(transaction?.total_credit_price) + toNumber(transaction?.final_amount);

        return acc;
      },
      {
        fuelPurchased: 0,
        totalLiters: 0,
        discounts: 0,
        plbCreditAmount: 0,
      }
    );
  }, [filteredInvoiceTransactions]);

  const vatableSalesRaw = useMemo(() => {
    return toNumber(invoiceSummary.plbCreditAmount) / VAT_DIVISOR;
  }, [invoiceSummary.plbCreditAmount]);

  const vatableSales = useMemo(() => getRoundedNumber(vatableSalesRaw), [vatableSalesRaw]);
  const vatAmount = useMemo(() => getRoundedNumber(vatableSalesRaw * VAT_RATE), [vatableSalesRaw]);
  const totalSalesVat = useMemo(
    () => getRoundedNumber(invoiceSummary.plbCreditAmount),
    [invoiceSummary.plbCreditAmount]
  );

  const invoiceGuideView = useMemo(() => {
    return {
      soldTo: createdInvoiceGuide?.soldTo || fleet?.businessName || "-",
      address: createdInvoiceGuide?.address || fleet?.businessAddress || fleet?.address || "-",
      tin: createdInvoiceGuide?.tin || "-",
      date:
        formatPeriodFromApi(createdInvoiceGuide?.periodStart, createdInvoiceGuide?.periodEnd) ||
        invoiceDateLabel,
      totalLiters: createdInvoiceGuide?.totalLiters ?? invoiceSummary.totalLiters,
      fuelPurchased: createdInvoiceGuide?.fuelPurchased ?? invoiceSummary.fuelPurchased,
      discounts: createdInvoiceGuide?.discounts ?? invoiceSummary.discounts,
      plbCreditAmount: createdInvoiceGuide?.plbCreditAmount ?? invoiceSummary.plbCreditAmount,
      vatableSales: createdInvoiceGuide?.vatableSales ?? vatableSales,
      vatAmount: createdInvoiceGuide?.vatAmount ?? vatAmount,
      totalSalesVat: createdInvoiceGuide?.totalSalesVat ?? totalSalesVat,
      lessVat: createdInvoiceGuide?.lessVat ?? vatAmount,
      amountNetOfVat: createdInvoiceGuide?.amountNetOfVat ?? vatableSales,
      addVat: createdInvoiceGuide?.addVat ?? vatAmount,
      totalAmountDue: createdInvoiceGuide?.totalAmountDue ?? totalSalesVat,
    };
  }, [
    createdInvoiceGuide,
    fleet?.address,
    fleet?.businessAddress,
    fleet?.businessName,
    invoiceDateLabel,
    invoiceSummary.discounts,
    invoiceSummary.fuelPurchased,
    invoiceSummary.plbCreditAmount,
    invoiceSummary.totalLiters,
    totalSalesVat,
    vatAmount,
    vatableSales,
  ]);

  const isCreateEnabled = useMemo(() => {
    const hasBusinessId = Boolean(businessId?.value?.trim());
    const hasActiveFleet = Boolean(fleet?.businessName);
    const hasInvoiceType = Boolean(invoiceType?.value);
    const hasPeriod = Boolean(selectedPeriod?.periodStart && selectedPeriod?.periodEnd);
    const hasCustomEndDate = invoiceType?.value !== "custom" || isCustomEndDateSelected;
    const hasCompany = Boolean(company);
    const hasProduct = Boolean(product);

    return (
      hasBusinessId &&
      hasActiveFleet &&
      hasInvoiceType &&
      hasPeriod &&
      hasCustomEndDate &&
      hasCompany &&
      hasProduct &&
      !loadingFleetDetails &&
      !loadingInvoiceTransactions &&
      !creatingInvoiceGuide
    );
  }, [
    businessId?.value,
    company,
    fleet?.businessName,
    invoiceType?.value,
    isCustomEndDateSelected,
    creatingInvoiceGuide,
    loadingFleetDetails,
    loadingInvoiceTransactions,
    product,
    selectedPeriod?.periodEnd,
    selectedPeriod?.periodStart,
  ]);

  const createInvoiceGuideCb = useCallback(async () => {
    if (!isCreateEnabled) {
      return;
    }

    const shortName = fleet?.shortName || businessId?.value?.trim();
    const selectedExemptionSet = new Set(exemptedTransactions.map((item) => `${item.value}`));

    const exemptions = transactionOptions.map((option = {}) => {
      const transactionId = Number(option.value);
      return {
        source: option.transaction?.source,
        transactionId,
        isReceiptIssued: selectedExemptionSet.has(`${option.value}`),
      };
    });

    try {
      const params = {
        shortName,
        invoiceType: invoiceType?.value,
        periodStart: selectedPeriod?.periodStart,
        periodEnd: selectedPeriod?.periodEnd,
        company,
        product,
      };
      const createdGuideResponse = await createInvoiceGuideRequest(params);

      setCreatedInvoiceGuide(createdGuideResponse || null);

      invoiceGuideModal.show({});
    } catch (error) {
      const apiErrorMessage =
        error?.data?.message ||
        "";

      if (
        `${apiErrorMessage}`
          .toLowerCase()
          .includes("this business id is not under single invoicing")
      ) {
        applyFieldErrors({
          businessId: apiErrorMessage,
        });
      }
    }
  }, [
    applyFieldErrors,
    businessId?.value,
    company,
    createInvoiceGuideRequest,
    exemptedTransactions,
    fleet?.shortName,
    invoiceGuideModal,
    invoiceType?.value,
    isCreateEnabled,
    product,
    selectedPeriod?.periodEnd,
    selectedPeriod?.periodStart,
    transactionOptions,
  ]);

  return (
    <div>
      <ConfirmModal
        {...customInvoiceModal}
        title={locale.generateCustomInvoice}
        content={locale.generateCustomInvoiceContent}
        onClose={() => {
          console.log("close");
          modifyField("invoiceType", { value: null });
          customInvoiceModal.close();
        }}
        primary={{
          text: locale.proceed,
          onClick: () => {
            modifyField("invoiceMonth", { value: null });
            modifyField("invoiceYear", { value: null });
            customInvoiceModal.close();
          },
        }}
        secondary={{
          text: locale.cancel,
          onClick: () => {
            modifyField("invoiceType", { value: null });
            customInvoiceModal.close();
          },
        }}
      />

      <ConfirmModal
        {...exemptedTransactionModal}
        onClose={onCancelExemptedChanges}
        title={locale.changeReceiptIssuanceTagging}
        content={`The receipt issuance tagging for ${changedSelectionCount} transaction${
          changedSelectionCount === 1 ? "" : "s"
        } will be\nupdated. Do you want to proceed?`}
        primary={{
          text: locale.proceed,
          onClick: onProceedExemptedChanges,
        }}
        secondary={{
          text: locale.cancel,
          onClick: onCancelExemptedChanges,
        }}
      />

      <SingleInvoicingGuideModal
        invoiceGuideModal={invoiceGuideModal}
        invoiceGuideView={invoiceGuideView}
      />

      <Intro
        title={locale.createSingleInvoiceGuide}
        actionText={locale.invoiceHistory}
        actionOutlined
        actionStartIcon={<HistoryIcon />}
        actionOnClick={() => {
          push(Path.PLBSingleInvoicingHistory);
        }}
      ></Intro>
      <div>
        <Field
          className={styles.field}
          labelClassName={styles.fieldLabel}
          horizontal
          {...businessId}
        >
          <TextField
            className={styles.longField}
            {...businessId}
            onChange={onChangeBusinessIdCb}
            loading={loadingFleetDetails}
          />
        </Field>

        {fleet?.businessName && (
          <>
            <Field
              className={styles.field}
              labelClassName={styles.fieldLabel}
              horizontal
              {...businessName}
            >
              <Text style={{ color: "#34384b" }}>{fleet.businessName}</Text>
            </Field>

            <Field className={styles.field} labelClassName={styles.fieldLabel} horizontal {...invoiceType}>
              <Dropdown
                name={invoiceType?.name}
                className={styles.longField}
                placeholder={locale.selectInvoiceType}
                type={"simple"}
                onChange={(value, name) => {
                  modifyField(name, { value });
                }}
                {...invoiceType}
                options={INVOICE_TYPE_OPTIONS}
              />
            </Field>

            {invoiceType?.value && (
              <Field className={styles.field} labelClassName={styles.fieldLabel} horizontal label={locale.invoicePeriod}>
                {isMonthlySelected ? (
                  <div style={{ display: "flex", gap: "12px", width: "100%" }}>
                    <div style={{ flex: 1 }}>
                      <Dropdown
                        name={invoiceMonth?.name}
                        placeholder="Month"
                        onChange={(value, name) => {
                          modifyField(name, { value });
                        }}
                        {...invoiceMonth}
                        options={MONTH_OPTIONS}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Dropdown
                        name={invoiceYear?.name}
                        placeholder="Year"
                        onChange={(value, name) => {
                          modifyField(name, { value });
                        }}
                        {...invoiceYear}
                        options={yearOptions}
                      />
                    </div>
                    <div style={{ flex: 1 }}></div>
                  </div>
                ) : null}

                {isCustomSelected ? (
                  <DateRange
                    name={invoicePeriod?.name}
                    value={invoicePeriod?.value}
                    onChange={(name, { value }) => {
                      const hasExistingCustomStartDate = Boolean(invoicePeriod?.value?.startDate);
                      if (!hasExistingCustomStartDate && value?.startDate) {
                        setIsCustomEndDateSelected(false);
                      } else if (hasExistingCustomStartDate) {
                        setIsCustomEndDateSelected(true);
                      }
                      modifyField(name, { value });
                    }}
                  />
                ) : null}
              </Field>
            )}

            {shouldShowTransactionFields && (
              <>
                <Field
                  className={styles.field}
                  labelClassName={styles.fieldLabel}
                  horizontal
                  label={locale.exemptedTransactions}
                >
                  <DropdownPaginatedSingleInvoicing
                    name="exemptedTransactions"
                    className={styles.longField}
                    placeholder={locale.select}
                    isMultiple
                    showSelectAll
                    maxLength={SEARCH_MAX_LENGTH}
                    request={async () => transactionOptions}
                    options={transactionOptions}
                    formatRequest={(result) => result || []}
                    loading={loadingInvoiceTransactions}
                    total={transactionOptions.length}
                    allLabel={locale.transactions}
                    allSelectedText={locale.transactions}
                    selectAllLabel={locale.selectAll}
                    value={exemptedTransactions}
                    isAll={
                      transactionOptions.length > 0 &&
                      exemptedTransactions.length === transactionOptions.length
                    }
                    closeToken={closeExemptedDropdownToken}
                    onCloseAttempt={handleExemptedCloseAttempt}
                    onChange={(_, { value, isAll }) => {
                      if (isAll) {
                        setExemptedTransactions(
                          normalizeSelection(
                            transactionOptions.map((option) => ({
                              value: option.value,
                              label: option.label,
                            }))
                          )
                        );
                        return;
                      }

                      setExemptedTransactions(normalizeSelection(value));
                    }}
                  />
                </Field>

                <Field
                  className={styles.field}
                  labelClassName={styles.fieldLabel}
                  horizontal
                  label={locale.company}
                >
                  <Dropdown
                    name="company"
                    className={styles.longField}
                    placeholder={locale.selectCompany}
                    onChange={(value) => {
                      setCompany(value);
                    }}
                    value={company}
                    options={COMPANY_OPTIONS}
                  />
                </Field>

                <Field
                  className={styles.field}
                  labelClassName={styles.fieldLabel}
                  horizontal
                  label={locale.product}
                >
                  <Dropdown
                    name="product"
                    className={styles.longField}
                    placeholder={locale.selectProduct}
                    onChange={(value) => {
                      setProduct(value);
                    }}
                    value={product}
                    options={PRODUCT_OPTIONS}
                  />
                </Field>
              </>
            )}
          </>
        )}
      </div>

      {isCreateEnabled && (
        <div className={styles.actions}>
          <Button
            primary
            loading={creatingInvoiceGuide}
            disabled={!isCreateEnabled}
            onClick={createInvoiceGuideCb}
          >
            Create
          </Button>
        </div>
      )}
    </div>
  );
};

export default PLBSingleInvoicingModule;